/**
 * The build, as a list of steps.
 *
 * <h3>Why the sequence is data</h3>
 *
 * The approved surface is a spine of nodes: the platform's steps resolve
 * themselves, the person's steps stop it until answered, and the order on
 * screen is the order things happen. Written as branching code that is a tangle
 * nobody can check against the design. Written as a list it can be read beside
 * the mockup and compared line for line, which is the only way a surface like
 * this stays the one that was approved.
 *
 * <h3>What a step is</h3>
 *
 * {@code key}      how the answer is stored, and what the backend call reads
 * {@code ask}      the question as a person reads it, plus its hint and input
 * {@code node}     what the trail records once it is answered — a title, the
 *                  quieter line under it, and a tone when the answer means the
 *                  record will sit at something other than done
 *
 * Platform steps are not in here. They are recorded when the call that performs
 * them returns, never when the sequence reaches them: an ExteLet onboard is one
 * call, so claiming "identity issued" before it has run would be predicting.
 */

import {
  FUNCTION_MODE_OPTIONS, INDUSTRY_MODE_OPTIONS, OPERATION_MODE_OPTIONS,
  EXECUTION_MODE_OPTIONS,
} from './agentModes';
/* The Register stage's steps — order, labels and icons — are the platform's,
   owned by data/registerSteps.js and read by the registration wizard too, so
   the console and Protect cannot disagree about what Register is. */
import { registerStep } from './registerSteps';
import { SVID_STATUS } from './svidStatus';

/** Framework choices, labelled as a person would say them, valued as the backend knows them. */
export const FRAMEWORKS = [
  { label: 'Anthropic · Claude', platformType: 'ANTHROPIC' },
  { label: 'Microsoft · Copilot', platformType: 'MICROSOFT_COPILOT' },
  { label: 'OpenAI · ChatGPT', platformType: 'OPENAI' },
  { label: 'Google · Gemini', platformType: 'VERTEX_AI' },
  { label: 'LangChain · LangGraph', platformType: 'LANGGRAPH' },
];

const ownerNode = (intent) => (intent?.ownerMode === 'nominate'
  ? {
    title: 'Owner nominated',
    text: 'The record stays NOMINATED until they accept for themselves.',
    tone: 'hold',
  }
  : {
    title: 'You accepted accountability',
    text: 'Claimed and attested in one step — the record is ACTIVE.',
  });

/**
 * How calls reach the agent, in the platform's own two values.
 *
 * `integrationPath` is CODED or MCP on the onboard payload
 * (ExteletRegistrationWizard.jsx:333). The labels are the console's, so the two
 * surfaces describe the same choice the same way.
 */
/**
 * The console's Connect step, field for field.
 *
 * <h3>Why all six and not two</h3>
 *
 * The spine used to ask for a name and a vendor id. The other four were not
 * therefore unset: the onboard sent the backend's defaults and a literal
 * `1.0.0`, so an agent registered through Protect carried an invocation
 * contract nobody chose and nobody was shown. They are optional on the console
 * and optional here — pre-filled with the console's own values, and sent as
 * answered.
 *
 * <h3>proxiedOnly</h3>
 *
 * The vendor agent id and the invocation pattern describe an agent the gateway
 * dispatches to. A coded-SDK agent runs in the customer's own process and calls
 * the gateway itself; the backend sets its id to the minted Agent UUID. The
 * console hides both for those frameworks and so does this, which is why the
 * marker is on the field rather than written into a branch somewhere.
 */
export const INVOCATION_PATTERNS = [
  { value: 'SYNCHRONOUS', label: 'SYNCHRONOUS \u2014 request / response (recommended for LLM calls)' },
  { value: 'ASYNCHRONOUS', label: 'ASYNCHRONOUS \u2014 job submit + poll' },
  { value: 'STREAMING', label: 'STREAMING \u2014 server-sent events' },
  { value: 'WEBHOOK', label: 'WEBHOOK \u2014 fire-and-forget' },
];

export const CONNECT_FIELDS = [
  { key: 'name', label: 'Display name', placeholder: 'partner-llm-7' },
  { key: 'externalAgentVersion', label: 'Version', value: '1.0.0' },
  { key: 'externalAgentId', label: 'Its id in that framework', proxiedOnly: true,
    placeholder: 'partner-llm-7 or claude-3-5-sonnet-20241022' },
  { key: 'invocationPattern', label: 'Invocation pattern', value: 'SYNCHRONOUS',
    proxiedOnly: true, options: INVOCATION_PATTERNS },
  { key: 'maxTokensPerRequest', label: 'Max tokens / request', type: 'number', value: 8192 },
  { key: 'timeoutSeconds', label: 'Timeout (seconds)', type: 'number', value: 300 },
];

export const PATHS = [
  { value: 'CODED', label: 'Coded agent \u2014 you own the source' },
  { value: 'MCP', label: 'Off-the-shelf \u2014 route through the MCP gateway' },
];

export const integrationPathFor = (label) =>
  PATHS.find((p) => p.label === label)?.value || 'CODED';

/**
 * The ExteLet spine, step for step against the console's registration wizard.
 *
 * <h3>Where the commit is</h3>
 *
 * `commit: true` marks the last question before anything is created. The
 * console submits between its step 4 and step 5, and so does this: everything
 * above it is still a draft the person can change, and everything below it is
 * reporting what already happened. A step that claims work before the call
 * that performs it has returned is a prediction, not a record.
 *
 * <h3>Why steps carry a stage</h3>
 *
 * The journey strip says where the AGENT is. Until the onboard returns there is
 * no agent, so every question before the commit sits at the first stage however
 * many of them have been answered.
 */
export const EXTELET_STEPS = [
  {
    key: 'framework',
    stage: 'discovery',
    icon: registerStep('framework').icon,
    ask: {
      question: 'Which framework does it run on?',
      hint: 'Auth, health probes and routing come from the connection behind it.',
      input: 'choice',
      options: FRAMEWORKS.map((f) => f.label),
      confirm: 'Bind framework',
    },
    node: (v) => ({
      title: 'Framework chosen',
      text: `${v}. Nothing is registered yet.`,
    }),
  },
  {
    /* Whose connection, not where it answers. The console binds to a Platform
       Connector an admin configured and refuses to continue without one;
       Protect offers Prove7's, or one the customer adds through the same
       connector API, health-checked before anything is bound to it. */
    key: 'connection',
    stage: 'discovery',
    /* The console folds resolving the connector into binding the framework, so
       this shares that step's icon rather than being the one question in the
       stage with a round number beside it. */
    icon: registerStep('framework').icon,
    ask: {
      question: 'Whose connection should it run through?',
      hint: 'Auth, health probes and routing come from the connection \u2014 ours, or one you '
          + 'add here.',
      input: 'connection',
    },
    node: (v) => ({
      title: v?.provided ? 'Bound to a Prove7 connection' : 'Bound to your connection',
      text: `${v?.label || 'Connection'} \u2014 credentials stay on the connection and never `
          + 'travel with a call.',
    }),
  },
  {
    key: 'agent',
    stage: 'discovery',
    icon: registerStep('connect').icon,
    ask: {
      question: 'What is it called, and how should its calls run?',
      hint: 'The name is yours. Everything under it is the console\u2019s own Connect step, '
          + 'opened on the values it pre-fills \u2014 change them or leave them.',
      input: 'fields',
      fields: CONNECT_FIELDS,
      confirm: 'Use these',
    },
    node: (v) => ({
      title: 'Named',
      text: `${v?.name || 'This agent'}${v?.externalAgentId ? ` \u00b7 ${v.externalAgentId}` : ''}`,
    }),
  },
  {
    key: 'path',
    stage: 'discovery',
    icon: registerStep('path').icon,
    ask: {
      question: 'How should calls reach it?',
      hint: 'Anything that does not take this path is refused.',
      input: 'choice',
      options: PATHS.map((p) => p.label),
      confirm: 'Choose the path',
    },
    node: (v) => ({
      title: 'Path chosen',
      text: integrationPathFor(v) === 'MCP'
        ? 'It will point its calls at the gateway. Its own code does not change.'
        : 'The SDK wraps its entry point, so request and response are both on the record.',
    }),
  },
  {
    key: 'accountability',
    stage: 'discovery',
    icon: registerStep('accountability').icon,
    /* The last question before anything exists. */
    commit: true,
    ask: {
      question: 'Who answers for it?',
      hint: 'A named human. Accountability is accepted, not assigned \u2014 and nothing is '
          + 'registered until this is answered.',
      input: 'accountability',
      confirm: 'Register it',
    },
    node: ownerNode,
  },
  {
    key: 'credentials',
    stage: 'discovery',
    icon: registerStep('credentials').icon,
    ask: {
      question: 'Save its keys. They are shown once.',
      hint: 'Only hashes are kept \u2014 nothing can show you the full key again.',
      input: 'credentials',
    },
    node: (v) => ({
      title: 'Credentials minted',
      text: `${(v?.keys || []).join(' \u00b7 ') || 'Keys'} \u2014 scoped to this agent alone.`,
    }),
  },
  {
    key: 'wire',
    stage: 'discovery',
    icon: registerStep('wire').icon,
    ask: {
      question: 'Wire it up.',
      hint: 'The last thing that happens outside Prove7.',
      input: 'snippet',
    },
    node: (v) => ({
      title: 'Wiring taken',
      text: v?.language === 'mcp'
        ? 'Gateway configuration copied. Calls arrive protected from the first one.'
        : `${v?.language === 'python' ? 'Python' : 'Java'} snippet copied.`,
    }),
  },
  {
    /* The console's last registration step. It does not ask anything — it
       reports where the registration actually stands, which is the one thing
       Protect never said: the row is on file as PENDING_VERIFICATION and the
       first protected call verifies it with nothing to press. Somebody who is
       not told that has no reason to make the call that would verify it. */
    key: 'registered',
    stage: 'discovery',
    icon: registerStep('activate').icon,
    ask: {
      question: 'It is registered.',
      hint: 'Where its registration stands, and what moves it on.',
      input: 'registered',
    },
    node: (v) => (v?.registrationStatus === 'PENDING_VERIFICATION'
      ? { title: 'Registered \u00b7 awaiting its first call',
        text: 'On file and protected. The first protected call it makes verifies it.' }
      : { title: 'Registered',
        text: v?.registrationStatus
          ? `Its registration is ${v.registrationStatus}.`
          : 'On file and protected.' }),
  },
  {
    /* Journey stage 02, and the first step that is about the AGENT rather than
       about registering it. The platform assigned the name during the onboard;
       this shows it. Nothing is asked — identity is not a decision — but a
       stage whose definition is "cryptographic name stamped" has to put the
       name on screen rather than claim it in a sentence. */
    key: 'identity',
    stage: 'identity',
    ask: {
      question: 'The name it will answer to.',
      hint: 'Assigned at registration and carried on every call it makes. Nothing to decide '
          + '\u2014 worth seeing once.',
      input: 'identity',
    },
    /* The state the certificate was actually in, not how one gets issued.
       Which mechanism applies is the platform's answer — with the SPIRE
       Workload API unwired nothing is issued by presenting — and the step
       above reads the capability and says the right one. A node asserting the
       other would contradict the step directly above it in the same trail. */
    node: (v) => {
      /* Three answers, not one. A refused read, an agent with no name yet,
         and a name we never asked for all arrived here as "no spiffeId" and
         left as the same sentence — which sent somebody to a page that, in
         the last case, shows the name perfectly well. */
      if (v?.unreadable) {
        return { title: 'Identity could not be read',
          text: 'The platform refused or could not answer the read. Its name is on the '
              + 'agent\u2019s own page in the full platform.', tone: 'hold' };
      }
      if (!v?.spiffeId) {
        return { title: 'No identity assigned yet',
          text: 'Nothing has stamped this agent with a name it can be recognised by.',
          tone: 'hold' };
      }
      if (v.status === SVID_STATUS.ACTIVE) {
        return { title: 'Identity stamped',
          text: 'Its SPIFFE name, with a live certificate behind it.' };
      }
      if (v.status === SVID_STATUS.EXPIRING) {
        return { title: 'Identity stamped \u00b7 certificate expiring',
          text: 'Its SPIFFE name. The certificate behind it expires shortly.',
          tone: 'hold' };
      }
      if (v.status === SVID_STATUS.EXPIRED) {
        return { title: 'Identity stamped \u00b7 certificate expired',
          text: 'Its SPIFFE name holds. The certificate behind it has expired.',
          tone: 'hold' };
      }
      return { title: 'Identity stamped \u00b7 no certificate yet',
        text: 'Its SPIFFE name. No certificate has been issued against it.',
        tone: 'hold' };
    },
  },
  {
    key: 'intent',
    stage: 'intent',
    ask: {
      question: 'What is it allowed to decide?',
      hint: 'Its Semantic Decision Layer. Attach one that is approved, describe one and send '
          + 'it for approval, or attach one later.',
      input: 'intent',
    },
    node: (v) => {
      if (v?.bound) {
        return { title: 'Decision layer attached',
          text: `${v.label} — its resolved skills are frozen on the binding.` };
      }
      if (v?.submitted) {
        return { title: 'Decision layer drafted · approval pending',
          text: `${v.label} is with an approver. Nothing is attached until they approve it.`,
          tone: 'hold' };
      }
      return { title: 'No decision layer yet',
        text: 'Attach one whenever it is ready. Until then the readiness gate reports it '
            + 'unmet.', tone: 'hold' };
    },
  },
  {
    /* Journey stage 04, and one of the readiness gate's nine checks. The gate
       counts grants that are not approval duties, so an approver alone leaves
       nobody able to run it — which is why this step says so rather than
       counting heads. */
    key: 'access',
    stage: 'access',
    ask: {
      question: 'Who may run it?',
      hint: 'At least one person or group needs real access. Naming an approver is a duty, '
          + 'not access, and the readiness gate does not count it.',
      input: 'access',
    },
    node: (v) => {
      if (v?.satisfied) {
        return { title: 'Access granted',
          text: `${v.granted} grant${v.granted === 1 ? '' : 's'} with runtime access. The `
              + 'readiness gate is met on access.' };
      }
      return { title: 'Nobody can run it yet',
        text: 'The readiness gate reports access unmet until somebody has view, execute, '
            + 'manage or admin on it.', tone: 'hold' };
    },
  },
  {
    /* Promote, and not the wizard's Register step 7 despite sharing its name.
       That step is a summary screen reporting PENDING_VERIFICATION; this one
       calls `gelMarkExecutionReady` (ActivateAsk.jsx:37), which is the
       Execution Ready promotion gate — journey stage 05. Two different actions
       wearing one word. */
    /* Build phase of Promote. An ExteLet's capability comes from its live
       connector, and nothing in this walk used to give it one — every agent
       finished with no skills, so its intent binding froze an empty list and
       every governed call was refused for having nothing to authorise. This is
       asked before "put it to work" because the readiness gate checks the rules
       that follow from these skills. */
    key: 'skills',
    stage: 'promote',
    ask: {
      question: 'What can it actually do?',
      hint: 'Its tools come from the connector it runs on. Tick the actions it may '
          + 'use and they become protected skills — each one then passes the gate '
          + 'on every call.',
      input: 'connectorSkills',
    },
    node: (v) => (v?.skillCount
      ? { title: `${v.skillCount} protected skill${v.skillCount === 1 ? '' : 's'}`,
        text: 'Each one is bound to its connector and passes the gate on every call.' }
      : { title: 'No protected skills yet',
        text: 'Nothing it declares can be authorised, so its calls will be refused '
            + 'until it has some.', tone: 'hold' }),
  },
  {
    /* Build phase still, and BEFORE the measuring step on purpose. The backend
       attributes every rule it materializes to the agent's one and only
       policy_skill (ExternalAgentPolicyBridge.resolveSolePolicySkillId:253);
       with none it attributes nothing and the reconcile pass at :308-320 never
       runs, so a limit the tenant later switches off keeps enforcing. This
       policy also has to be measured to leave the build phase at all
       (PhaseGateService.promoteEvalReadyBuildSkills:348), and nothing can
       review a skill still in build — so it is created while the measuring
       step is still ahead of it. */
    key: 'policy',
    stage: 'promote',
    ask: {
      question: 'What holds it to account?',
      hint: 'Its protection hangs on one policy of its own. The limits you set next '
          + 'attach to it, which is what lets you change or lift them later — and it '
          + 'is what you review before this agent may act.',
      input: 'policySkill',
    },
    node: (v) => (v?.policyId
      ? { title: v.policyName || 'Protection policy on file',
        text: 'Its limits are attributed here, and this is what gets reviewed before '
            + 'it may act.' }
      : { title: 'Nothing to attribute protection to',
        text: 'Limits set now could not be changed or lifted afterwards.', tone: 'hold' }),
  },
  {
    /* Still Promote's Build phase. The readiness gate asks whether any
       protection rules are in force, and nothing in this walk used to put any
       there — the service that does it had no caller, so the gate could never
       pass from this surface. Asked after the skills because the rules are
       about what those skills may do. */
    key: 'limits',
    stage: 'promote',
    ask: {
      question: 'What must it never do?',
      hint: 'These become rules the gate checks on every call — not instructions in a '
          + 'prompt it can talk its way around.',
      input: 'protectionLimits',
    },
    node: (v) => (v?.rules
      ? { title: `${v.rules} rule${v.rules === 1 ? '' : 's'} in force`,
        text: 'Checked at the boundary on every protected call.' }
      : { title: 'No limits in force', text: 'Nothing is held back yet.', tone: 'hold' }),
  },
  {
    /* Build phase still, and asked AFTER the limits: the policy is configured,
       its rules are materialized, and then everything is measured — the order
       the phase map states. The Build gate asks, per skill, for a completed
       eval suite scoring at or above 0.70 — another skill's eval does not
       count — and nothing in this walk ever ran one, so no agent built here
       could reach Decide. */
    key: 'evals',
    stage: 'promote',
    ask: {
      question: 'Does it do what it says?',
      hint: 'Each skill is measured before the agent may move on. A skill that is not '
          + 'measured, or scores too low, stays behind.',
      input: 'evals',
    },
    node: (v) => (v?.anyReady
      ? { title: `${v.measured} skill${v.measured === 1 ? '' : 's'} measured and passing`,
        text: 'Measured against the same bar the promotion gate applies.' }
      : { title: 'Nothing measured yet',
        text: 'Its skills cannot be reviewed or put to work until they are.', tone: 'hold' }),
  },
  {
    /* The advance the walk never had. Nothing here ever moved an agent's phase:
       the lifecycle calls were wired into the console's pages and the v2 detail
       tabs and into nothing on this surface, so an agent built here stayed in
       build for good and Execution Ready's phase_act check could never pass.
       An ExteLet cannot use the native advance at all — PhaseGateService.
       advancePhase refuses it outright — so this goes through the GEL
       lifecycle, which enforces the same gate it previews. */
    key: 'toDecide',
    stage: 'promote',
    ask: {
      question: 'Ready to decide how it runs?',
      hint: 'The platform checks what it can do and whether it has been measured. '
          + 'Everything on the list has to be clear before it can move on.',
      input: 'toDecide',
    },
    node: (v) => (v?.advancedTo
      ? { title: 'Moved to Decide',
        text: 'Its measured skills moved with it, and each one can now be reviewed.' }
      : { title: 'Still in Build',
        text: 'Nothing it holds can be reviewed or put to work from here.', tone: 'hold' }),
  },
  {
    /* The Decide-phase review. The Decide gate needs one protection skill
       reviewed and NOT blocked (hasNonBlockedReviewedSkill, enforced at
       ExternalAgentLifecycleService:249); nothing in this walk ever reviewed
       one, so Act was unreachable and so was Execution Ready. */
    key: 'review',
    stage: 'promote',
    ask: {
      question: 'How should each one run?',
      hint: 'On its own, with a person approving each call, or held back. At least one '
          + 'has to be allowed to run for the agent to act at all.',
      input: 'review',
    },
    node: (v) => (v?.reviewed
      ? { title: `${v.reviewed} skill${v.reviewed === 1 ? '' : 's'} reviewed`,
        text: 'Each one now runs the way somebody said it should.' }
      : { title: 'Nothing reviewed yet',
        text: 'Nobody has said how its skills may run, so it cannot act.', tone: 'hold' }),
  },
  {
    /* Decide → Act. Same component as toDecide — the two differ only in which
       gate is previewed and which lifecycle call commits it. */
    key: 'toAct',
    stage: 'promote',
    ask: {
      question: 'Let it act?',
      hint: 'The platform checks the limits in force, its autonomy ceiling and the '
          + 'reviews just made. Clear means it may act under them.',
      input: 'toAct',
    },
    node: (v) => (v?.advancedTo
      ? { title: 'Moved to Act',
        text: 'It may act under the limits in force. Execution Ready is what admits '
            + 'its calls to protected traffic.' }
      : { title: 'Still in Decide',
        text: 'It cannot be marked execution ready from here yet.', tone: 'hold' }),
  },

  {
    key: 'activate',
    stage: 'promote',
    ask: {
      question: 'Put it to work?',
      hint: 'Execution Ready is what admits its calls to protected traffic. The platform '
          + 'checks the same list it enforces, so anything unmet is shown before you ask.',
      input: 'activate',
    },
    node: (v) => (v?.activated
      ? { title: 'Execution Ready',
        text: 'Marked by the platform and sealed. Its calls are admitted to protected '
            + 'traffic from now on.' }
      : { title: 'Registered, not active', text: 'It is on the record and protected. Mark it '
          + 'execution ready whenever its gate is clear.', tone: 'hold' }),
  },
  {
    key: 'rules',
    stage: 'enforce',
    ask: {
      question: 'What must never leave?',
      hint: 'Each one is a rule your tenant already has. Switching it on applies it at the '
          + 'boundary, not in the model\u2019s instructions.',
      input: 'boundaries',
    },
    node: (v) => (v?.labels?.length
      ? {
        title: `${v.labels.length} boundary rule${v.labels.length === 1 ? '' : 's'} on`,
        text: `${v.labels.join(' \u00b7 ')} \u2014 enforced at the boundary.`,
      }
      : { title: 'No boundary rules on', text: 'Nothing is held back yet. They can be switched '
          + 'on at any time.', tone: 'hold' }),
  },
  {
    key: 'probe',
    stage: 'enforce',
    ask: {
      question: 'One live call, on the record.',
      hint: 'Nothing is proved by a probe it never ran.',
      input: 'choice',
      options: ['Run it now', 'Not yet'],
      confirm: 'Finish',
    },
    /* This step does not run the call — it takes you to where you make it. A
       node claiming "recorded and sealed" above a call nobody made would be
       the one lie this surface exists to prevent. */
    node: (v) => (v === 'Run it now'
      ? { title: 'Ready for its first call',
        text: 'Opening the Run Center, with the verdict shown under the answer.' }
      : { title: 'No call yet', text: 'Until one runs, nothing has been proved end to end.',
        tone: 'hold' }),
  },
];

/* ── an agent your team authored here ──────────────────────────────────── */

/**
 * The Let spine, against `AgentCreateSlideout` and the create it posts.
 *
 * <h3>What it asks, and what it leaves alone</h3>
 *
 * The form requires a name and types the agent on four dimensions; everything
 * else it offers — description, endpoint, gateway URL, API key — is optional
 * and belongs to an agent that already exists. So the spine asks for what the
 * create needs, and the rest is edited on the agent's own page afterwards.
 *
 * <h3>Why autonomous is not on the list</h3>
 *
 * `ck_autonomous_op_requires_full_trust` is a CHECK constraint, and a new
 * agent's trust_band defaults to SUPERVISED — trust is earned from governed
 * runs, not chosen at create time. Offering it here would mean collecting the
 * whole form and then failing on a constraint violation, which is the worst
 * moment to learn the rule.
 */
export const LET_STEPS = [
  {
    key: 'naming',
    stage: 'identity',
    ask: {
      question: 'What is it called, and what is it for?',
      hint: 'The name is how everyone will refer to it. The description is for the people '
          + 'who come after you.',
      input: 'fields',
      fields: [
        { key: 'name', label: 'Name', placeholder: 'IAM Sentry' },
        { key: 'description', label: 'What it is responsible for', placeholder: 'Optional' },
      ],
      confirm: 'Use these',
    },
    node: (v) => ({
      title: 'Named',
      text: v?.description ? `${v.name} \u2014 ${v.description}` : (v?.name || 'Named'),
    }),
  },
  {
    key: 'modes',
    stage: 'identity',
    ask: {
      question: 'What kind of agent is this?',
      hint: 'Four dimensions the platform types it on. Each one is checked at the database, '
          + 'so a value outside them is refused rather than stored.',
      input: 'choices',
      groups: [
        { key: 'functionMode', label: 'What it does', options: FUNCTION_MODE_OPTIONS },
        { key: 'industryMode', label: 'Where it works', options: INDUSTRY_MODE_OPTIONS },
        {
          key: 'operationMode',
          label: 'Who drives it',
          options: OPERATION_MODE_OPTIONS.filter((o) => o.value !== 'autonomous'),
          note: 'Autonomous needs Full trust, which is earned from protected runs. A new agent '
              + 'starts Supervised, so it cannot hold it yet.',
        },
        { key: 'executionMode', label: 'How it runs', options: EXECUTION_MODE_OPTIONS },
      ],
      confirm: 'Type it',
    },
    node: (v) => ({
      title: 'Typed',
      text: [v?.functionMode, v?.industryMode, v?.operationMode, v?.executionMode]
        .filter(Boolean).join(' \u00b7 '),
    }),
  },
  {
    key: 'accountability',
    stage: 'identity',
    commit: true,
    ask: {
      question: 'Who answers for it?',
      hint: 'A named human. Nothing is created until this is answered.',
      input: 'accountability',
    },
    node: ownerNode,
  },
  {
    key: 'rules',
    stage: 'enforce',
    ask: {
      question: 'What must never leave?',
      hint: 'Each one is a rule your tenant already has. Switching it on applies it at the '
          + 'boundary, not in the model\u2019s instructions.',
      input: 'boundaries',
    },
    node: (v) => (v?.labels?.length
      ? {
        title: `${v.labels.length} boundary rule${v.labels.length === 1 ? '' : 's'} on`,
        text: `${v.labels.join(' \u00b7 ')} \u2014 enforced at the boundary.`,
      }
      : { title: 'No boundary rules on', text: 'Nothing is held back yet. They can be switched '
          + 'on at any time.', tone: 'hold' }),
  },
  {
    key: 'probe',
    stage: 'enforce',
    ask: {
      question: 'One live call, on the record.',
      hint: 'Nothing is proved by a call nobody made.',
      input: 'choice',
      options: ['Run it now', 'Not yet'],
      confirm: 'Finish',
    },
    node: (v) => (v === 'Run it now'
      ? { title: 'Ready for its first call',
        text: 'Opening the Run Center, with the verdict shown under the answer.' }
      : { title: 'No call yet', text: 'Until one runs, nothing has been proved end to end.',
        tone: 'hold' }),
  },
];

/* ── an agent drafted from what you describe ───────────────────────────── */

/**
 * The AutoLet spine, against `AutoCreateLauncher` and the run it starts.
 *
 * <h3>Why it does not ask who answers for it</h3>
 *
 * Starting the pipeline creates no agent — it drafts one, and the agent is
 * created at review. Asking who is accountable for a thing that may never exist
 * puts the question in the wrong place; it is asked where the agent is real.
 *
 * <h3>Why the spine ends at the commit</h3>
 *
 * From the moment the run starts, its own event stream owns the trail. Two
 * producers writing the same feed interleave into nonsense.
 */
export const AUTOLET_STEPS = [
  {
    key: 'words',
    stage: 'identity',
    ask: {
      question: 'Describe what it should do.',
      hint: 'Whatever you would tell a new colleague on their first day. We draft it step by '
          + 'step and stop at anything we cannot work out.',
      input: 'fields',
      fields: [{ key: 'sourceRef', label: 'In your words' }],
      confirm: 'Use this',
    },
    node: (v) => ({
      title: 'Brief taken',
      text: v?.sourceRef ? `\u201c${String(v.sourceRef).slice(0, 90)}\u2026\u201d` : 'Brief taken',
    }),
  },
  {
    key: 'naming',
    stage: 'identity',
    ask: {
      question: 'What should it be called?',
      hint: 'A name you will recognise in a list of fifty.',
      input: 'fields',
      fields: [{ key: 'name', label: 'Name', placeholder: 'Renewals Assistant' }],
      confirm: 'Use this name',
    },
    node: (v) => ({ title: 'Named', text: v?.name || 'Named' }),
  },
  {
    key: 'modes',
    stage: 'identity',
    commit: true,
    ask: {
      question: 'What kind of agent should it draft?',
      hint: 'These are hints to the pipeline. Each is checked at the database, so a value '
          + 'outside them is refused rather than stored.',
      input: 'choices',
      groups: [
        { key: 'functionMode', label: 'What it does', options: FUNCTION_MODE_OPTIONS },
        { key: 'industryMode', label: 'Where it works', options: INDUSTRY_MODE_OPTIONS },
        {
          key: 'operationMode',
          label: 'Who drives it',
          options: OPERATION_MODE_OPTIONS.filter((o) => o.value !== 'autonomous'),
          note: 'Autonomous needs Full trust, which is earned from protected runs. A drafted '
              + 'agent starts Supervised, so it cannot hold it yet.',
        },
        {
          key: 'executionMode',
          label: 'How it runs',
          /* `auto` is the pipeline choosing from the source, which is the
             launcher's own default and the reason this list differs from the
             native one. */
          options: [
            { value: 'auto', label: 'auto \u2014 let the pipeline choose from the source' },
            ...EXECUTION_MODE_OPTIONS,
          ],
          note: null,
        },
      ],
      confirm: 'Start drafting',
    },
    node: () => ({
      title: 'Drafting started',
      text: 'Every step it takes appears below as it happens.',
    }),
  },
];

/** The spine for an archetype, or nothing when we do not have one for it. */
/**
 * The key the protection-policy step files its answer under.
 *
 * <p>Named here rather than retyped at the reader, because the reader got it wrong once: it
 * looked up {@code answers.policySkill} — the step's INPUT component — while answers are keyed
 * by the step's KEY. Always undefined, silently, so the policy somebody chose never reached the
 * call that attributes the rules to it, and they materialized unattributed.</p>
 */
export const POLICY_STEP_KEY = 'policy';

/**
 * The protection policy the person chose, out of the walk's answers.
 *
 * <p>Null when they were never asked — an agent with one policy, where the platform resolves the
 * sole one. Null is the right answer there and must not become a string.</p>
 */
export function policySkillIdFrom(answers) {
  return answers?.[POLICY_STEP_KEY]?.policyId || null;
}

export function scriptFor(archetype) {
  const key = String(archetype || '').toLowerCase();
  if (key === 'extelet') return EXTELET_STEPS;
  if (key === 'let') return LET_STEPS;
  if (key === 'autolet') return AUTOLET_STEPS;
  /* No guessing. Walking somebody down the registration spine for a thing that
     is not registered is worse than having no spine for it. */
  return null;
}

export default EXTELET_STEPS;

/**
 * What is already true about an agent being picked back up.
 *
 * <h3>Read, not replayed</h3>
 *
 * A resumed agent's original answers are gone — given in another browser,
 * perhaps by somebody else, perhaps months ago. Running each step's `node()`
 * over invented answers would produce a trail that LOOKED like a record of
 * what somebody chose while actually being a reconstruction of what we
 * guessed. So each cleared stage states the platform's own fact and no more
 * than that.
 *
 * <h3>No step key</h3>
 *
 * These nodes carry none. A step key is what lets the trail offer to change a
 * step — and nobody answered these here, so there is nothing to reopen. The
 * place to change them is the stage itself, further down.
 *
 * @param {Object} done `{identity, intent, access}` as the platform reports it
 */
/**
 * A resumed agent's trail, from what its operator actually answered.
 *
 * <h3>What this replaces, and why the old reasoning was right</h3>
 *
 * <p>{@link clearedTrail} builds a resumed trail from platform facts because, as its own comment
 * says, "a resumed agent's original answers are gone — given in another browser, perhaps by
 * somebody else, perhaps months ago", and running each step's {@code node()} over invented
 * answers would produce something that looked like a record while being a reconstruction.</p>
 *
 * <p>That reasoning stands. Its premise does not: each answer is now kept with the agent as it is
 * given, so the trail can state what was answered rather than what can be inferred. Measured
 * before the change: fourteen answers came back as eight nodes, and Register — where the agent
 * was named, pointed at a framework, wired up and handed its keys — came back as one line.</p>
 *
 * <h3>Still read, never replayed</h3>
 *
 * <p>A stored answer is shown in the words it was stored with. Nothing re-runs a step's
 * {@code node()} over it: that would make this build's rendering of somebody else's answer, and
 * the point of a record is that it says what they said.</p>
 *
 * <p>A stage with no stored answers falls back to the platform's fact for it, so an agent that
 * predates the record looks exactly as it does today rather than emptier.</p>
 *
 * @param {Array}  answers rows from `GET /agents/{id}/build-answers`, any order
 * @param {Object} done    `{identity, intent, access}` as the platform reports it
 */
export function trailFromAnswers(answers, done = {}) {
  const rows = Array.isArray(answers) ? answers : [];
  if (!rows.length) return clearedTrail(done);

  const answered = [...rows]
    .sort((a, b) => (a?.ordinal ?? 0) - (b?.ordinal ?? 0))
    .map((r, n) => ({
      /* No stepKey. That is what lets the trail offer to change a step, and these were answered
         elsewhere — possibly by somebody else. Offering "Change" on a step this session never
         walked is its own decision and not this one. */
      id: `answered-${r?.stepKey || n}`,
      stage: r?.stage || null,
      title: r?.label || null,
      text: r?.detail || null,
    }));

  /* Only for stages the record says nothing about. A stage that has answers must not also carry
     the platform's one-line summary of itself — that duplicate is what made Register read as a
     single line. */
  const covered = new Set(answered.map((n) => n.stage).filter(Boolean));
  const fallback = clearedTrail(done).filter((n) => !covered.has(n.stage));

  return [...answered, ...fallback];
}

export function clearedTrail(done = {}) {
  /* Registration is true of any agent that exists — there is no agent to
     resume otherwise. */
  const out = [{
    id: 'cleared-discovery',
    stage: 'discovery',
    title: 'Registered',
    text: 'It is on the record, with its own row and its own history.',
  }];

  if (done.identity) {
    out.push({
      id: 'cleared-identity',
      stage: 'identity',
      title: 'Identity stamped',
      text: 'It carries a SPIFFE name no other agent has.',
    });
  }
  if (done.intent) {
    out.push({
      id: 'cleared-intent',
      stage: 'intent',
      title: 'Decision layer attached',
      text: 'It is bound to an approved Intent Profile version.',
    });
  }
  if (done.access) {
    out.push({
      id: 'cleared-access',
      stage: 'access',
      title: 'Access granted',
      text: 'Somebody holds runtime access to it.',
    });
  }
  return out;
}
